/**
 * KhiProtectionCard — какво още може да защити бъбрека (пропуснати възможности).
 * Данни: GET /api/khi/protection-check
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Info, CircleSlash, HelpCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ST = {
  missing: { color: "#EF4444", label: "ПРОПУСНАТО", Icon: ShieldAlert },
  blocked: { color: "#F59E0B", label: "ЗАСЕГА НЕ", Icon: CircleSlash },
  ok: { color: "#10B981", label: "НАРЕД", Icon: ShieldCheck },
  info: { color: "#0EA5E9", label: "ЗА ОБСЪЖДАНЕ", Icon: Info },
  unknown: { color: "#94A3B8", label: "НЯМА ДАННИ", Icon: HelpCircle },
};

export default function KhiProtectionCard({ cardCls, isDark }) {
  const [data, setData] = useState(null);
  const [showOk, setShowOk] = useState(false);
  const [bp, setBp] = useState({ s: "", d: "" });
  const [savingBp, setSavingBp] = useState(false);

  const load = () => axios.get(`${API}/khi/protection-check`)
    .then(({ data }) => setData(data))
    .catch(() => setData(null));

  useEffect(() => { load(); }, []);

  const saveBp = async () => {
    if (!bp.s || !bp.d) return;
    setSavingBp(true);
    try {
      await axios.post(`${API}/bp/log`, {
        systolic: Number(bp.s),
        diastolic: Number(bp.d),
      });
      await load();
    } finally {
      setSavingBp(false);
    }
  };

  if (!data?.items?.length) return null;
  const items = showOk ? data.items : data.items.filter((i) => i.status !== "ok");

  return (
    <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="khi-protection-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            Защита на бъбрека
          </h3>
          <p className="text-[11px] opacity-60 mt-0.5">
            Какво още може да забави увреждането — по общите нефрологични насоки
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-500"
            data-testid="khi-protection-missing-count">
            {data.missing_count} пропуснати
          </span>
          <button onClick={() => setShowOk((v) => !v)}
            className="text-[10px] font-semibold opacity-60 hover:opacity-100 underline"
            data-testid="khi-protection-toggle-ok">
            {showOk ? "скрий наред" : `покажи наред (${data.ok_count})`}
          </button>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {items.map((i) => {
          const s = ST[i.status] || ST.info;
          return (
            <div key={i.code} className="rounded-xl p-3"
              style={{ background: s.color + "12", borderLeft: `3px solid ${s.color}` }}
              data-testid={`khi-protection-item-${i.code}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[12px] font-bold">{i.title_bg}</span>
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                  style={{ background: s.color + "22", color: s.color }}>
                  <s.Icon className="w-3 h-3" /> {s.label}
                </span>
              </div>
              <div className="text-[12px] mt-1 opacity-90">{i.text_bg}</div>
              {i.why_bg && <div className="text-[10px] mt-1 opacity-55">{i.why_bg}</div>}
              {i.code === "bp" && i.status !== "ok" && (
                <div className="flex items-center gap-1.5 mt-2">
                  <input type="number" value={bp.s} placeholder="130"
                    onChange={(e) => setBp((p) => ({ ...p, s: e.target.value }))}
                    className={`w-16 px-2 py-1 rounded-lg text-[12px] border ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
                    data-testid="khi-bp-systolic" />
                  <span className="opacity-50">/</span>
                  <input type="number" value={bp.d} placeholder="80"
                    onChange={(e) => setBp((p) => ({ ...p, d: e.target.value }))}
                    className={`w-16 px-2 py-1 rounded-lg text-[12px] border ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
                    data-testid="khi-bp-diastolic" />
                  <button onClick={saveBp} disabled={savingBp}
                    className="text-[11px] font-bold px-3 py-1 rounded-lg bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-60"
                    data-testid="khi-bp-save-btn">
                    {savingBp ? "Запазвам…" : "Запази"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-[9px] opacity-50 mt-2">{data.disclaimer_bg}</div>
    </div>
  );
}
